import _ from 'lodash';

/**
 * Recursively compares two objects and returns an array of changes
 * @param {Object} original - The original object
 * @param {Object} updated - The updated object
 * @param {string} prefix - The prefix for nested keys (used internally)
 * @returns {Array} Array of objects with fieldName, oldValue, and newValue
 */
export const getChangedFields = (original, updated, prefix = '') => {
  const changes = [];

  // Get all unique keys from both objects
  const allKeys = new Set([
    ...Object.keys(original || {}),
    ...Object.keys(updated || {}),
  ]);

  // List of keys to skip in comparisons (metadata, pagination, audit fields, etc.)
  const keysToSkip = [
    'stages',
    'dispositions',
    'dependentKPIRules',
    'nextToken', // Pagination token
    '__typename', // GraphQL metadata
    'createdAt',
    'updatedAt',
    'createdBy',
    'lastUpdatedBy',
    '_version', // AWS AppSync versioning
    '_lastChangedAt', // AWS AppSync sync
    '_deleted', // AWS AppSync soft delete
    'items', // Pagination items that might have nextToken
  ];

  allKeys.forEach((key) => {
    // Skip certain keys that shouldn't be compared
    if (keysToSkip.includes(key)) {
      return;
    }

    const originalValue = original?.[key];
    const updatedValue = updated?.[key];

    // Calculate the full field name with prefix
    const fieldName = prefix ? `${prefix}.${key}` : key;

    // If values are objects (but not null) and not arrays, recurse
    if (
      _.isPlainObject(originalValue) &&
      _.isPlainObject(updatedValue) &&
      !Array.isArray(originalValue) &&
      !Array.isArray(updatedValue)
    ) {
      const nestedChanges = getChangedFields(
        originalValue,
        updatedValue,
        fieldName
      );
      changes.push(...nestedChanges);
    } else {
      // Compare values
      if (!_.isEqual(originalValue, updatedValue)) {
        changes.push({
          fieldName: formatFieldName(fieldName),
          oldValue: formatValue(originalValue),
          newValue: formatValue(updatedValue),
        });
      }
    }
  });

  return changes;
};

/**
 * Formats a field name to be more readable
 * @param {string} fieldName - The field name
 * @returns {string} Formatted field name
 */
const formatFieldName = (fieldName) => {
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/\./g, ' > ') // Replace dots with arrows
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

/**
 * Formats a value for display
 * @param {*} value - The value to format
 * @returns {string} Formatted value
 */
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '(empty)';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'string' && value === '') {
    return '(empty)';
  }
  return String(value);
};
